import { useCallback, useEffect, useRef, useState } from "react";

const BOTTOM_SLACK = 40;

/**
 * Keeps a scroll container pinned to the bottom while content grows (a streaming reply), until the
 * user does something else: scrolls up, uses the keyboard to scroll, or clicks a control in the
 * conversation (e.g. expands "Thinking"). Following resumes when they scroll back to the bottom, or
 * when the page calls `toBottom` (after sending a message).
 *
 * `ref` is a callback ref: the container often appears only after loading, and the listeners
 * must be attached to it then.
 */
export function useStickToBottom(dep: unknown) {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const stuck = useRef(true);

  useEffect(() => {
    if (!el) return;
    const atBottom = () => el.scrollHeight - el.scrollTop - el.clientHeight < BOTTOM_SLACK;
    const release = () => {
      stuck.current = false;
    };
    const onScroll = () => {
      if (atBottom()) stuck.current = true;
    };
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) release();
    };
    const onKey = (e: KeyboardEvent) => {
      if (["ArrowUp", "PageUp", "Home"].includes(e.key)) release();
    };
    const onPointer = (e: PointerEvent) => {
      if ((e.target as HTMLElement).closest("button, a, summary, [role=button]")) release();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("wheel", onWheel, { passive: true });
    el.addEventListener("touchmove", release, { passive: true });
    el.addEventListener("keydown", onKey);
    el.addEventListener("pointerdown", onPointer);
    el.scrollTop = el.scrollHeight;
    return () => {
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchmove", release);
      el.removeEventListener("keydown", onKey);
      el.removeEventListener("pointerdown", onPointer);
    };
  }, [el]);

  useEffect(() => {
    if (el && stuck.current) el.scrollTop = el.scrollHeight;
  }, [dep, el]);

  const toBottom = useCallback(() => {
    stuck.current = true;
    if (el) el.scrollTop = el.scrollHeight;
  }, [el]);

  return { ref: setEl, toBottom };
}
